const createStorageKeys = <const T extends Readonly<Record<string, string>>>(keys: T): T =>
    new Proxy(keys, {
        get(target, prop, receiver) {
            if (typeof prop === 'string' && !(prop in target)) {
                throw new Error(`[STORAGE_KEYS] Missing key access: ${prop}`);
            }
            return Reflect.get(target, prop, receiver);
        },
    }) as T;

const STORAGE_KEYS_BASE = Object.freeze({
    // User Identity
    USER_ID: 'userId',
    LEGACY_USER_ID: 'amp_user_id',
    
    DEVICE_ID: 'amp_device_id',
    SESSION_ID: 'amp_session_id',
    LAST_EVENT_TIME: 'amp_last_event_time',
    AUTH_TOKEN: 'authToken',

    // Game State
    DAILY_GAME_PREFIX: 'daily_game_', // Usage: `${DAILY_GAME_PREFIX}${gameId}`

    // Streak
    USER_STREAK: 'user_streak',
    SHOW_STREAK_CELEBRATION: 'show_streak_celebration',

    // Leaderboard
    USER_SCHOOL_ID: 'user_school_id',
    USER_SCHOOL_NAME: 'user_school_name',
    USER_SCHOOL_GROUP_ID: 'user_school_group_id',

    // Theme
    THEME: 'theme',

		// Cached API responses
		MARCH_YAKNESS_CACHE: 'march_yakness_cache',
		MARCH_YAKNESS_STATS_CACHE: 'march_yakness_stats_cache',

		// Injected by iOS webview
		APP_USER_ID: 'APP_USER_ID',
		APP_AUTH_TOKEN: 'APP_AUTH_TOKEN',
} as const);

export const STORAGE_KEYS = createStorageKeys(STORAGE_KEYS_BASE);

export type StorageKey = keyof typeof STORAGE_KEYS;

export const getDailyGameKey = (gameId: string, dateId?: string) => {
    if (dateId) {
        return `${STORAGE_KEYS.DAILY_GAME_PREFIX}${gameId}:${dateId}`;
    }
    return `${STORAGE_KEYS.DAILY_GAME_PREFIX}${gameId}`;
};

/**
 * Safely gets the user ID from localStorage, falling back to legacy keys if needed
 * and migrating them to the current key.
 */
export const getUserIdFromStorage = (): string | null => {
    if (typeof window === 'undefined') return null;

    const current = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (current) return current;

    const injected = localStorage.getItem(STORAGE_KEYS.APP_USER_ID);
    if (injected) return injected;

    const legacy = localStorage.getItem(STORAGE_KEYS.LEGACY_USER_ID);
    if (legacy) {
        localStorage.setItem(STORAGE_KEYS.USER_ID, legacy);
        localStorage.removeItem(STORAGE_KEYS.LEGACY_USER_ID);
        return legacy;
    }

    return null;
};
